import React, { forwardRef, useCallback, useMemo } from 'react'
import cx from 'classnames'
import * as Icon from '@manychat/icons'

import * as l from '../../../utils/localization/format'
import AvatarRound from '../../AvatarRound'
import BtnV2 from '../../BtnV2'
import { OptionState } from '../../forms/OptionsList'
import Select from '../../forms/Select'
import List from '../../List'
import MainNavigationItem from '../MainNavigationItem'

import { groupAccountOptions } from './groupAccountOptions'
import { AccountOption, AccountSelectProps } from './interfaces'

import cm from './AccountSelect.module.css'

export const AccountSelect = forwardRef<HTMLButtonElement, AccountSelectProps>(
  (
    {
      accounts,
      selectedAccount,
      onAccountChange,
      onAccountPinToggle,
      onAccountAddClick,
      onSelectAccountClick,
      placeholder = l.translate('Select account'),
      className,
      isCollapsed,
      isLoading,
    }: AccountSelectProps,
    ref: React.Ref<HTMLButtonElement | null>,
  ) => {
    const groupedOptions = useMemo(() => groupAccountOptions(accounts), [accounts])

    const showSearch = accounts.length >= 5

    const renderOption = useCallback(
      (
        option: AccountOption,
        optionState: OptionState,
        optionProps: React.HTMLAttributes<HTMLElement>,
      ) => {
        if (option.type === 'group') {
          return <div className={cm.accountGroup}>{option.label}</div>
        }

        const IconComponent: keyof typeof Icon = option.isPinned ? 'Unpin' : 'Pin'
        const avatarUrl = option?.avatarUrl || ''

        return (
          <List.Item
            {...option}
            selected={optionState.selected}
            {...optionProps}
            size="large"
            leftContent={<AvatarRound src={avatarUrl} />}
            lozenge={option.lozenge}
            className={cm.accountSelectOption}
            contentClassName={cm.accountSelectOptionContent}
            rightContent={
              <BtnV2
                ariaLabel={
                  option.isPinned ? l.translate('Unpin account') : l.translate('Pin account')
                }
                onlyIcon={IconComponent}
                size="small"
                variant="ghost-light"
                onClick={(event) => {
                  event.stopPropagation()
                  onAccountPinToggle(option, !option.isPinned)
                }}
              />
            }
          />
        )
      },
      [onAccountPinToggle],
    )

    const renderAnchor = useCallback(
      (selectedOption: AccountOption | null) => {
        const avatarUrl = selectedOption?.avatarUrl || ''

        return (
          <MainNavigationItem
            as="div"
            data-test-id="account-select-anchor"
            collapsed={isCollapsed}
            icon={<AvatarRound src={avatarUrl} lozenge={selectedOption?.lozenge} />}
            ariaLabel={l.translate('Select account')}
            onClick={(event) => {
              event.preventDefault()
              onSelectAccountClick?.()
            }}
          >
            <div className="d-flex">
              <span className={cm.accountSelectNavItemContent}>
                {isCollapsed ? undefined : selectedOption?.label || placeholder}
              </span>
              <Icon.ChevronDown size={16} className={cm.accountSelectNavItemChevronIcon} />
            </div>
          </MainNavigationItem>
        )
      },
      [isCollapsed, placeholder, onSelectAccountClick],
    )

    const handleChange = useCallback(
      (account: AccountOption) => {
        onAccountChange?.(account)
      },
      [onAccountChange],
    )

    const renderAddAccountButton = useCallback(() => {
      return (
        <div className="p-a-xs b-t">
          <BtnV2
            onClick={(event) => {
              event.stopPropagation()
              onAccountAddClick?.()
            }}
            variant="default"
            size="small"
            label={l.translate('New account')}
            leftIcon="Plus"
            fullWidth
            disabled={isLoading}
          />
        </div>
      )
    }, [onAccountAddClick, isLoading])

    return (
      <Select
        ref={ref}
        options={groupedOptions as AccountOption[]}
        value={selectedAccount}
        onChange={handleChange}
        placeholder={placeholder}
        label=""
        search={showSearch}
        popoverWidth={320}
        emptyLabel={l.translate('No accounts found')}
        searchLabel={l.translate('Search accounts')}
        className={cx(cm.accountSelectContainer, className)}
        renderOption={renderOption}
        renderAnchor={renderAnchor}
        renderCustomLastComponent={renderAddAccountButton}
        loading={isLoading}
      />
    )
  },
)
